import { useEffect, useState } from 'react';

import { Outlet, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useStripe } from '@stripe/react-stripe-js';

import ConfirmationModal from '../components/billing/modal/ConfirmationModal';
import { useUserDetialsContext } from '../context/user-details';
import { ReactComponent as Spinner } from './spinner.svg';

import confirmSetupIntent from '../utils/billing/confirmSetupIntent';
import { confirmPayment } from '../utils/billing/confirmPayment';
import redirectToLogin from '../utils/token';
import getAuthToken from '../utils/cookie';
import { verify } from '../utils/verify';
import CardInputModal from '../components/billing/modal/CardInputModal';

function SimpleLayout() {
  const [tokenVerified, setTokenVerified] = useState(false);
  const { updateUserDetails } = useUserDetialsContext();

  const stripe = useStripe();
  const navigate = useNavigate();

  const [paymentMethodId, setPaymentMethodId] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [isTrialingIntent, setIsTrialingIntent] = useState(false);

  const suretyClickHandler = (choice) => {
    const isYes = choice === 'yes';
    setConfirmationModal(false);
    if (isYes) {
      if (!stripe || !paymentMethodId || !clientSecret) {
        console.log('Stripe, PaymentMethodId, or ClientSecret is null');
        return;
      }
      if (isTrialingIntent) {
        confirmSetupIntent(stripe, clientSecret, paymentMethodId);
      } else {
        confirmPayment(stripe, navigate, clientSecret, paymentMethodId);
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const tokenValue = getAuthToken();
      if (!tokenValue) {
        return redirectToLogin();
      }

      const data = await verify(tokenValue);
      if (!data) {
        return redirectToLogin();
      }

      const {
        email,
        oid,
        given_name,
        status,
        custom_book_template,
        custom_script_template,
        site_tour,
        book_token,
        script_token,
        extra_book_tokens,
        extra_script_tokens,
        action,
        start_date,
        canceled,
        current_period_start,
        canceled_at,
        current_period_end,
        show_chat,
        company_name,
        company_id
      } = data;

      const baseUserDetails = {
        name: given_name,
        email,
        oid,
        image: null,
        tokenId: tokenValue,
        status,
        custom_book_template,
        custom_script_template,
        site_tour,
        show_chat,
        company_name,
        company_id,
        isIndividualUser: !company_id
      };

      const handlePaymentAction = () => {
        if (action === 'requires_payment_method') {
          const { card, client_secret } = data;
          if (card) {
            setClientSecret(client_secret);
            setShowCardModal(true);
          } else {
            toast.error(
              'Please update your card details. https://portal.fullfr.me/p/login/8wM6s5edibn46vm5kk'
            );
          }
        } else if (action === 'requires_action') {
          const { client_secret, payment_method } = data;
          setClientSecret(client_secret);
          setPaymentMethodId(payment_method);
          setConfirmationModal(true);
          if (status === 'trialing') {
            setIsTrialingIntent(true);
          }
        }
      };

      const extendedUserDetails = {
        ...baseUserDetails,
        canceled,
        start_date,
        current_period_start,
        canceled_at,
        current_period_end,
        script_tokens: script_token,
        book_tokens: book_token,
        extra_book_tokens,
        extra_script_tokens
      };

      switch (status) {
        case 'not_subscribed':
          updateUserDetails(baseUserDetails);
          break;
        case 'canceled':
          updateUserDetails(extendedUserDetails);
          break;
        case 'past_due':
        case 'incomplete':
        case 'trialing':
          handlePaymentAction();
          updateUserDetails({ ...extendedUserDetails, action });
          break;
        default:
          updateUserDetails(extendedUserDetails);
      }
    };
    fetchData().then(() => {
      setTokenVerified(true);
    });
  }, []);

  return (
    <main id="pageContainer" className="w-full z-20">
      {!tokenVerified ? (
        <div className="flex items-center justify-center h-screen">
          <Spinner className="h-28 w-28 sm:h-40 sm:w-40 !text-gray-500 fill-primary animate-spin" />
          <span className="sr-only">Loading...</span>
        </div>
      ) : (
        <div>
          <Outlet />
          <ConfirmationModal
            confirmationModal={confirmationModal}
            setConfirmationModal={setConfirmationModal}
            suretyClickHandler={suretyClickHandler}
            subHeading="Confirm Subscription"
            message="Please confirm you would like to start your paid subscription for $25? You will be charged immediately and receive an additional 10 script credits and 5 book credits."
          />
        </div>
      )}
      <CardInputModal
        clientSecret={clientSecret}
        showCardModal={showCardModal}
        setShowCardModal={setShowCardModal}
      />
    </main>
  );
}

export default SimpleLayout;
